import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { locationTrackQuery } from '../api/protectee'
import { loadGoogleMaps, type GMap, type GMapMarker, type GMapPolyline, type GoogleMaps } from '../easeedesk/googleMaps'
import { dayMonthYear, stamp } from '../inventoree/format'
import { Spinner } from '../ui/Spinner'

interface Pt {
  id: number
  lat: number
  lng: number
  label: string
  address: string
}

/** `moment(x).format('D MMM YYYY, HH:mm')`, ca la coloana Date din tabel. */
function fmt(date: string): string {
  const when = stamp(date)
  if (!when) return date
  return `${dayMonthYear(when)}, ${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`
}

/**
 * Harta cu drumul aparatului in timp, in fila Location history a unei masini.
 *
 * Se incarca DOAR aici - nu pe Summary, nu la fiecare deschidere de masina - ca
 * statiile clientilor sa cheme Google numai cand cineva chiar deschide istoricul
 * de locatii (vezi nota din `DeviceTabs.tsx`). Cheia vine de la server
 * (`GOOGLE_MAPS_KEY`), ca la GPS tracking; cand lipseste, harta spune ca lipseste
 * si tabelul de dedesubt ramane intreg.
 *
 * Trage de bara ca sa vezi unde a fost la fiecare moment: punctul rosu si traseul
 * albastru cresc pana la clipa aleasa; A = primul punct, B = ultimul.
 */
export function LocationMap({ id }: { id: number }) {
  const q = useQuery(locationTrackQuery(id))
  const box = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<GMap | null>(null)
  const dotRef = useRef<GMapMarker | null>(null)
  const trailRef = useRef<GMapPolyline | null>(null)
  const [ready, setReady] = useState(false)
  const [idx, setIdx] = useState(0)
  const [mapError, setMapError] = useState('')

  const pts = useMemo<Pt[]>(() => {
    const out: Pt[] = []
    for (const r of q.data?.points ?? []) {
      const lat = Number(r.latitude)
      const lng = Number(r.longitude)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
      out.push({ id: r.id, lat, lng, label: fmt(r.date), address: r.address ?? '' })
    }
    return out
  }, [q.data])

  const n = pts.length
  const mapsKey = q.data?.mapsKey ?? ''

  /* La prima venire a datelor, pornim de la ultimul punct (cel mai nou). */
  useEffect(() => {
    if (n > 0) setIdx(n - 1)
  }, [n])

  /* Harta se face o singura data, cand avem puncte si cheie. */
  useEffect(() => {
    if (!box.current || mapRef.current || n === 0) return
    if (mapsKey === '') {
      setMapError('no-key')
      return
    }
    let gone = false
    void loadGoogleMaps(mapsKey).then(
      (maps: GoogleMaps) => {
        if (gone || !box.current) return
        const first = pts[0]!
        const last = pts[n - 1]!
        const map = new maps.Map(box.current, {
          zoom: 13,
          center: { lat: last.lat, lng: last.lng },
          mapTypeControl: false,
          streetViewControl: false,
        })
        mapRef.current = map

        /* Tot drumul, sters (gri). */
        new maps.Polyline({
          path: pts.map((p) => ({ lat: p.lat, lng: p.lng })),
          geodesic: true,
          strokeColor: '#9aa0a6',
          strokeOpacity: 0.6,
          strokeWeight: 2,
          map,
        })
        /* Traseul care creste pana la clipa aleasa (albastru). */
        trailRef.current = new maps.Polyline({
          path: [],
          geodesic: true,
          strokeColor: '#2f6fd8',
          strokeOpacity: 0.9,
          strokeWeight: 4,
          map,
        })

        new maps.Marker({ map, position: { lat: first.lat, lng: first.lng }, label: 'A', title: first.label })
        if (n > 1) {
          new maps.Marker({ map, position: { lat: last.lat, lng: last.lng }, label: 'B', title: last.label })
        }
        dotRef.current = new maps.Marker({
          map,
          position: { lat: last.lat, lng: last.lng },
          zIndex: 999,
          icon: {
            path: 0, // google.maps.SymbolPath.CIRCLE
            scale: 7,
            fillColor: '#d9534f',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        })

        if (n > 1) {
          const bounds = new maps.LatLngBounds()
          for (const p of pts) bounds.extend({ lat: p.lat, lng: p.lng })
          map.fitBounds(bounds)
        }

        setReady(true)
      },
      (e: Error) => {
        if (!gone) setMapError(e.message)
      },
    )
    return () => {
      gone = true
    }
  }, [pts, mapsKey, n])

  /* La miscarea barei: muta punctul rosu si creste traseul pana la el. */
  useEffect(() => {
    const map = mapRef.current
    const dot = dotRef.current
    const trail = trailRef.current
    if (!ready || !map || !dot || !trail || n === 0) return
    const i = Math.min(idx, n - 1)
    const p = pts[i]!
    dot.setPosition({ lat: p.lat, lng: p.lng })
    trail.setPath(pts.slice(0, i + 1).map((x) => ({ lat: x.lat, lng: x.lng })))
    map.panTo({ lat: p.lat, lng: p.lng })
  }, [idx, ready, pts, n])

  if (q.isPending) return <Spinner />
  /* Fara puncte, sau eroare la date: nicio harta; tabelul de jos ramane. */
  if (q.error || n === 0) return null

  const cur = pts[Math.min(idx, n - 1)]

  return (
    <div className="row">
      <div className="col-md-12">
        {mapError ? (
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle"></i>{' '}
            {mapError === 'no-key'
              ? 'The map cannot be drawn: no Google Maps key is configured for this installation.'
              : mapError}
          </div>
        ) : (
          <>
            <div ref={box} style={{ width: '100%', height: 360, border: '2px solid #ddd', marginBottom: 8 }} />
            {n > 1 && cur ? (
              <div style={{ margin: '0 4px 16px' }}>
                <input
                  type="range"
                  min={0}
                  max={n - 1}
                  step={1}
                  value={Math.min(idx, n - 1)}
                  onChange={(e) => setIdx(Number(e.target.value))}
                  style={{ width: '100%' }}
                  aria-label="Location history time"
                />
                <div className="text-muted" style={{ marginTop: 4 }}>
                  <i className="far fa-clock"></i> {cur.label}
                  {cur.address ? ` — ${cur.address}` : ''}
                  <span style={{ float: 'right' }}>
                    {Math.min(idx, n - 1) + 1} / {n}
                  </span>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
